import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth } from '../config/firebase';
import './FormPage.css';

type FeedbackCategory = 'bug' | 'suggestion' | 'compliment' | 'other';

function Feedback() {
  const { user } = useAuth();
  const [category, setCategory] = useState<FeedbackCategory>('suggestion');
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken();
      if (!idToken) throw new Error('Not authenticated');

      const res = await fetch(`${API_BASE}/users/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          feedback: feedback.trim(),
          category,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Something went wrong' }));
        throw new Error(body.error || `Failed (${res.status})`);
      }

      setSubmitted(true);
      setFeedback('');
    } catch (err: any) {
      setError(err.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="form-page">
        <header className="page-header">
          <h1>Thank you!</h1>
          <p>Your feedback has been received. We appreciate you helping us improve mosaime.</p>
        </header>
        <button
          className="btn btn-primary btn-full mt-lg"
          onClick={() => setSubmitted(false)}
        >
          Submit More Feedback
        </button>
      </div>
    );
  }

  return (
    <div className="form-page">
      <header className="page-header">
        <h1>Share Your Feedback</h1>
        <p>
          Having issues with SMS, want to suggest a feature, or just want to let us know
          how things are going? We'd love to hear from you, {user?.name || 'there'}.
        </p>
      </header>

      <form onSubmit={handleSubmit}>
        <section className="settings-section card">
          <h2>Category</h2>
          <div className="form-group">
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
            >
              <option value="bug">Bug / Something isn't working</option>
              <option value="suggestion">Suggestion / Feature request</option>
              <option value="compliment">Compliment / What's working well</option>
              <option value="other">Other</option>
            </select>
          </div>
        </section>

        <section className="settings-section card">
          <h2>Your Feedback</h2>
          <div className="form-group">
            <textarea
              id="feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell us what's on your mind..."
              rows={6}
              style={{ resize: 'vertical', minHeight: '120px' }}
            />
          </div>
        </section>

        {error && (
          <p style={{ color: '#d32f2f', textAlign: 'center', marginBottom: '16px' }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-full mt-lg"
          disabled={submitting || !feedback.trim()}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </form>
    </div>
  );
}

export default Feedback;
